import React, { useCallback, useState } from "react";
import { Alert, AlertTitle, Box, Button, CircularProgress } from "@mui/material";

import { Progress } from "ui";

import { useFeatureFlag } from "fond/featureFlags";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { LogViewerModal } from "fond/widgets";

interface GeneratePanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const stageConfig: Record<string, { label: string }> = {
  prepare: { label: "Preparing inputs" },
  bom: { label: "Counting materials and costs" },
  c2s: { label: "Calculating cost to serve" },
};

const GeneratePanel: React.FC<GeneratePanelProps> = ({ isReadOnly }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const { workflowState, stages, runWorkflow, logs } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.NBN_COST_TO_SERVE.ROOT,
    refreshVersionDataOnFail: true,
  });
  const isGeneratingCTS = workflowState === "STARTING" || workflowState === "IN_PROGRESS";
  const { value: isCustomWorkflowErrorLogsEnabled } = useFeatureFlag("custom_workflow_error_logs");

  const handleGenerateCTS = useCallback(async () => {
    runWorkflow?.();
  }, [runWorkflow]);

  const handleViewLogs = useCallback(() => setIsLogsModalOpen(true), []);

  return (
    <>
      {workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Cost to serve is ready to generate.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Cost to serve generated.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Cost to serve generation failed</AlertTitle>
          Cost to serve could not be generated.
          {isCustomWorkflowErrorLogsEnabled && (
            <Box mt={1}>
              {logs?.type === "waiting" && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={12} color="inherit" />
                  Attempting to process logs
                </Box>
              )}
              {logs?.type === "ready" && logs.logs.length === 0 && "Error logs are not available."}
              {logs?.type === "ready" && logs.logs.length > 0 && (
                <Button size="small" variant="outlined" color="error" onClick={handleViewLogs}>
                  View Logs
                </Button>
              )}
            </Box>
          )}
        </Alert>
      )}

      {stages && (isGeneratingCTS || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key].label, Status: status }))} />
      )}

      {workflowState !== "COMPLETE" && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!runWorkflow || isGeneratingCTS || isReadOnly}
          onClick={handleGenerateCTS}
        >
          Generate
        </Button>
      )}

      {isCustomWorkflowErrorLogsEnabled && logs?.type === "ready" && (
        <LogViewerModal
          open={isLogsModalOpen}
          onClose={() => setIsLogsModalOpen(false)}
          logs={logs.logs}
          title="Logs"
          aria-label="Error logs modal"
        />
      )}
    </>
  );
};

export default GeneratePanel;
